import React, { useState, useRef, useCallback } from "react";
import {
  Sparkles, Camera, Copy, Check, ChevronRight, Play,
  Instagram, Share2, Loader2, Flame, Zap, Gift, ArrowRight, Hash,
} from "lucide-react";

/**
 * Creator viral-output sub-components — extracted from CreatorsPage.js
 * in iter150 for readability. Behaviour preserved exactly, all
 * data-testids preserved for E2E parity.
 */


// ─── Before/After Slider ───
export const BeforeAfterSlider = ({ beforeSrc, afterSrc }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, x)));
  }, []);

  const onMouseDown = () => { dragging.current = true; };
  const onMouseUp = () => { dragging.current = false; };
  const onMouseMove = (e) => { if (dragging.current) handleMove(e.clientX); };
  const onTouchMove = (e) => { handleMove(e.touches[0].clientX); };

  return (
    <div ref={containerRef}
      className="relative w-full aspect-[4/5] sm:aspect-[3/4] max-w-md mx-auto rounded-2xl overflow-hidden cursor-col-resize select-none shadow-xl"
      onMouseDown={onMouseDown} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      onMouseMove={onMouseMove} onTouchMove={onTouchMove} onTouchStart={onMouseDown} onTouchEnd={onMouseUp}
      data-testid="before-after-slider">
      <img src={afterSrc} alt="After" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={beforeSrc} alt="Before" className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} />
      </div>
      <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-0.5 h-full bg-white shadow-lg" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center">
          <div className="flex gap-0.5">
            <ChevronRight size={12} className="text-[#1E1B4B] rotate-180" />
            <ChevronRight size={12} className="text-[#1E1B4B]" />
          </div>
        </div>
      </div>
      <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 text-white text-[10px] font-bold backdrop-blur-sm">BEFORE</span>
      <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#E83E8C]/80 text-white text-[10px] font-bold backdrop-blur-sm">AFTER</span>
    </div>
  );
};


// ─── Copy Button ───
export const CopyBtn = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium bg-white border border-gray-200 hover:border-[#E83E8C] transition-all" data-testid="copy-btn">
      {copied ? <><Check size={10} className="text-emerald-500" /> Copied!</> : <><Copy size={10} /> {label || "Copy"}</>}
    </button>
  );
};


// ═══════════════════════════════════════════════════════════════
// VIRAL OUTPUT SCREEN
// ═══════════════════════════════════════════════════════════════
export const ViralOutput = ({ transformedImage, originalImage, viralContent, onRegenerate, generating }) => {
  const vc = viralContent || {};
  const [selectedCaption, setSelectedCaption] = useState(0);
  const [showRedo, setShowRedo] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  const shareWithImage = async (platform) => {
    const caption = platform === "whatsapp"
      ? `${vc.captions?.[selectedCaption] || vc.captions?.[0] || "Check out my party transformation!"}\n\n${(vc.hashtags || []).join(" ")}\n\nTag @DoozyGo®`
      : `${vc.captions?.[selectedCaption] || vc.captions?.[0] || ""}\n\n${(vc.hashtags || []).join(" ")}`;

    if (transformedImage && navigator.share) {
      try {
        const blob = await fetch(`data:image/jpeg;base64,${transformedImage}`).then(r => r.blob());
        const file = new File([blob], "doozygo-transformation.jpg", { type: "image/jpeg" });
        await navigator.share({ text: caption, files: [file] });
        return;
      } catch (err) { /* fallback below */ }
    }
    if (platform === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank");
    } else {
      window.open(`https://www.instagram.com/doozygo`, "_blank");
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="viral-output">
      {/* Hero banner */}
      <div className="text-center py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] rounded-2xl">
        <p className="text-white font-bold text-lg flex items-center justify-center gap-2">
          <Flame size={20} /> Your House Party Just Went VIRAL <Flame size={20} />
        </p>
      </div>

      {/* Before/After Slider — only if transformed image exists */}
      {transformedImage ? (
        <BeforeAfterSlider beforeSrc={originalImage} afterSrc={`data:image/jpeg;base64,${transformedImage}`} />
      ) : (
        <div className="text-center py-8 bg-white/5 rounded-2xl border border-white/10">
          <Camera size={32} className="text-[#E83E8C]/40 mx-auto mb-2" />
          <p className="text-sm text-white/50">Image transformation is processing...</p>
          <p className="text-[10px] text-white/30 mt-1">The viral content plan below is ready — image may take a moment</p>
        </div>
      )}

      {/* Redo / Regenerate */}
      <div className="space-y-2" data-testid="redo-section">
        {!showRedo ? (
          <button onClick={() => setShowRedo(true)}
            className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-xs font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2" data-testid="redo-toggle">
            <Sparkles size={12} /> Not what you imagined? Customize & Regenerate
          </button>
        ) : (
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <p className="text-xs font-bold text-white/70">Tell us what to change:</p>
            <textarea value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="e.g. Add more balloons, make it more colorful, use pink and gold theme, add a neon sign saying Happy Birthday..."
              rows={3}
              className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-xs text-white placeholder-white/30 resize-none" data-testid="custom-prompt-input" />
            <div className="flex gap-2">
              <button onClick={() => { onRegenerate(customPrompt); setShowRedo(false); setCustomPrompt(""); }}
                disabled={generating}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1" data-testid="regenerate-btn">
                {generating ? <><Loader2 size={12} className="animate-spin" /> Regenerating...</> : <><Sparkles size={12} /> Regenerate</>}
              </button>
              <button onClick={() => { onRegenerate(""); setShowRedo(false); }} disabled={generating}
                className="px-4 py-2.5 rounded-xl bg-white/10 text-white/60 text-xs font-medium disabled:opacity-40" data-testid="redo-default-btn">
                Redo Default
              </button>
              <button onClick={() => setShowRedo(false)} className="px-3 py-2.5 rounded-xl text-white/30 text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Reel Plan */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <Play size={16} className="text-red-500" /> Your Viral Reel Plan
        </h3>
        {vc.reel_hook && (
          <div className="bg-red-50 rounded-xl p-3 mb-3 border border-red-100">
            <p className="text-[10px] font-bold text-red-600 uppercase mb-1">Hook (First 3 Seconds)</p>
            <p className="text-sm font-bold text-[#1E1B4B]">"{vc.reel_hook}"</p>
          </div>
        )}
        <div className="space-y-2">
          {(vc.shot_breakdown || []).map((shot, i) => (
            <div key={shot.shot} className="flex items-center gap-3 p-2 rounded-lg bg-[#FAFAFA]">
              <span className="w-6 h-6 rounded-full bg-[#E83E8C] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <div className="flex-1">
                <p className="text-xs font-bold text-[#1E1B4B]">{shot.shot}</p>
                <p className="text-[10px] text-[#94a3b8]">{shot.description}</p>
              </div>
              <span className="text-[10px] text-[#E83E8C] font-bold">{shot.duration}</span>
            </div>
          ))}
        </div>
        {vc.ending_cta && (
          <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-100">
            <p className="text-[10px] font-bold text-amber-600">Ending CTA: <span className="text-[#1E1B4B]">{vc.ending_cta}</span></p>
          </div>
        )}
      </div>

      {/* Captions */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-amber-500" /> Ready-to-Post Captions
        </h3>
        <div className="space-y-2">
          {(vc.captions || []).map((cap, i) => (
            <div key={`cap-${i}`} onClick={() => setSelectedCaption(i)}
              className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedCaption === i ? "border-[#E83E8C] bg-[#E83E8C]/5" : "border-gray-100 hover:border-gray-200"}`}>
              <p className="text-xs text-[#1E1B4B]">{cap}</p>
            </div>
          ))}
        </div>
        {vc.captions?.[selectedCaption] && (
          <div className="mt-3 flex justify-center">
            <CopyBtn text={vc.captions[selectedCaption]} label="Copy Caption" />
          </div>
        )}
      </div>

      {/* Hashtags */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <Hash size={16} className="text-blue-500" /> Trending Hashtags
        </h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {(vc.hashtags || []).map((tag) => (
            <span key={tag} className="px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">{tag}</span>
          ))}
        </div>
        <CopyBtn text={(vc.hashtags || []).join(" ")} label="Copy All Hashtags" />
      </div>

      {/* This Will Blow Up Because... */}
      <div className="bg-gradient-to-br from-[#1E1B4B] to-indigo-800 rounded-2xl p-5 text-white">
        <h3 className="text-sm font-bold flex items-center gap-2 mb-3">
          <Zap size={16} className="text-amber-400" /> This Will Blow Up Because...
        </h3>
        <div className="space-y-2">
          {(vc.why_viral || []).map((reason) => (
            <div key={reason} className="flex items-start gap-2">
              <ArrowRight size={12} className="text-[#E83E8C] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-white/80">{reason}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Share Trigger */}
      {vc.share_trigger && (
        <div className="text-center py-4 bg-[#E83E8C]/5 rounded-2xl border border-[#E83E8C]/20">
          <p className="text-lg font-bold text-[#E83E8C]">"{vc.share_trigger}"</p>
        </div>
      )}

      {/* Tag CTA */}
      <div className="text-center space-y-3 py-4">
        <p className="text-sm text-[#475569]">
          Tag <strong className="text-[#E83E8C]">@DoozyGo</strong> when you post — we might recreate this for you
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button onClick={() => shareWithImage("whatsapp")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white text-xs font-bold hover:opacity-90 shadow-lg" data-testid="share-whatsapp">
            <Share2 size={14} /> Share with Image
          </button>
          <button onClick={() => shareWithImage("instagram")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white text-xs font-bold hover:opacity-90 shadow-lg" data-testid="share-instagram">
            <Instagram size={14} /> Share on Instagram
          </button>
          {transformedImage && (
            <button onClick={() => {
              const link = document.createElement("a");
              link.href = `data:image/jpeg;base64,${transformedImage}`;
              link.download = "doozygo-transformation.jpg";
              link.click();
            }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold hover:bg-white/20 shadow-lg" data-testid="download-image">
              <ArrowRight size={14} className="rotate-90" /> Download Image
            </button>
          )}
        </div>
      </div>

      {/* Creator Reward */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-2xl border border-amber-200 p-5 text-center">
        <Gift size={24} className="text-amber-500 mx-auto mb-2" />
        <p className="text-sm font-bold text-amber-800">Top 10 creators this month get their party FREE</p>
        <p className="text-[10px] text-amber-600 mt-1">Post your transformation, tag @DoozyGo, and you're in the running!</p>
      </div>
    </div>
  );
};
